import { convertAddressToFormik } from "@/modules/address/addressFormikType"
import { type Address } from "@modules/address/addressTypes"
import { type BoxFormikType } from "@modules/box/boxFormikType"
import { BiweeklyPeriod, PayoutFrequencyType, WeeklyAnchorStripe } from "@modules/box/boxPayoutFrequencyTypes"
import { type BoxSettingsFormFields, type BoxWaiverFormFields } from "@modules/box/boxSettingsFormikType"
import { type PaymentMethodPreference } from "@modules/box/boxSettingsType"
import { BoxTypeOf, type BoxWaiver, type PhoneNumberWithPrefix } from "@modules/box/boxTypes"
import { type BoxDetailDto, type BoxDto, type BoxItemDto } from "@modules/box/services/boxDto"
import { BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { roleBoxStaffPermissions } from "@modules/boxStaff/utils/boxStaffUtils"
import { MediaType } from "@modules/common/media/mediaTypes"
import { convertImageFromDto } from "@modules/dtos/imageDto"
import { TaxSettingsStatus } from "@modules/finances/gateway/taxesDto"
import { StripeAccountErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { StripePlatform, type StripeAccountStatus } from "@modules/payment/stripeTypes"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { type HUImage } from "@modules/types/imageTypes"
import { type LocationDto } from "@modules/types/locationTypes"
import dayjs from "dayjs"

export type BoxMenuPermissions = {
  canSeeTimetables: boolean
  canSeeOffers: boolean
  canSeeMembers: boolean
  canSeeFinancial: boolean
  canSeeGymInformation: boolean
  canSeeShop: boolean
}

type BoxDetailsState = Omit<
  BoxDetailsEntity,
  "getBoxSettingsDraft" | "getBoxFormik" | "getBoxWaiverDraft" | "getPermissions"
>

export class BoxEntity {
  id: string
  name: string
  logo: HUImage | null

  private constructor(box: BoxEntity) {
    this.id = box.id
    this.name = box.name
    this.logo = box.logo
  }

  static fromDto(dto: BoxDto): BoxEntity {
    return new BoxEntity({
      id: dto.id,
      name: dto.name,
      logo: dto.logo ? convertImageFromDto(dto.logo) : null,
    })
  }
}

export class BoxDetailsEntity {
  _type: typeof BoxTypeOf
  id: string
  logo: HUImage
  boxPhotos: HUImage[]
  cover: HUImage
  name: string
  description: string
  address: Address
  phoneNumberWithPrefix: PhoneNumberWithPrefix
  email: string
  author: UserEntity // Refacto author to owner (No ticket created)
  siren: string
  isSubscribed: boolean
  stripeAccountStatus?: StripeAccountStatus
  stripeAccountCurrency?: string
  stripeMinimumAmount?: number
  stripeMaximumAmount?: number
  accountError?: StripeAccountErrorCode
  stripeAccountId?: string
  stripePlatform: StripePlatform
  currentUserRole?: BoxStaffRole
  location?: LocationDto
  slotGeneratorInWeeks?: number
  paymentMethodPreferences: PaymentMethodPreference[]
  zoneId: string | null
  waiver: BoxWaiver | null
  invitationLink: string | null
  showSessionAttendees: boolean
  taxStatus: TaxSettingsStatus | null
  payoutFrequencyType: PayoutFrequencyType
  payoutFrequencyDayCalendar: number | null
  weeklyAnchorStripe: WeeklyAnchorStripe | null
  biweeklyPeriod: BiweeklyPeriod | null
  createdAt: string | null

  private constructor(boxDetails: BoxDetailsState) {
    this._type = BoxTypeOf
    this.id = boxDetails.id
    this.logo = boxDetails.logo
    this.boxPhotos = boxDetails.boxPhotos
    this.cover = boxDetails.cover
    this.name = boxDetails.name
    this.description = boxDetails.description
    this.address = boxDetails.address
    this.phoneNumberWithPrefix = boxDetails.phoneNumberWithPrefix
    this.email = boxDetails.email
    this.author = boxDetails.author
    this.siren = boxDetails.siren
    this.isSubscribed = boxDetails.isSubscribed
    this.stripeAccountStatus = boxDetails.stripeAccountStatus
    this.stripeAccountCurrency = boxDetails.stripeAccountCurrency
    this.stripeMinimumAmount = boxDetails.stripeMinimumAmount
    this.stripeMaximumAmount = boxDetails.stripeMaximumAmount
    this.accountError = boxDetails.accountError
    this.stripeAccountId = boxDetails.stripeAccountId
    this.stripePlatform = boxDetails.stripePlatform
    this.currentUserRole = boxDetails.currentUserRole
    this.location = boxDetails.location
    this.slotGeneratorInWeeks = boxDetails.slotGeneratorInWeeks
    this.paymentMethodPreferences = boxDetails.paymentMethodPreferences
    this.zoneId = boxDetails.zoneId
    this.waiver = boxDetails.waiver
    this.invitationLink = boxDetails.invitationLink
    this.showSessionAttendees = boxDetails.showSessionAttendees
    this.taxStatus = boxDetails.taxStatus
    this.payoutFrequencyType = boxDetails.payoutFrequencyType
    this.payoutFrequencyDayCalendar = boxDetails.payoutFrequencyDayCalendar
    this.weeklyAnchorStripe = boxDetails.weeklyAnchorStripe
    this.biweeklyPeriod = boxDetails.biweeklyPeriod
    this.createdAt = boxDetails.createdAt
  }

  static fromDto(dto: BoxDetailDto): BoxDetailsEntity {
    return new BoxDetailsEntity({
      ...dto,
      _type: BoxTypeOf,
      logo: convertImageFromDto(dto.logo),
      boxPhotos: dto.photos.map((photo) => convertImageFromDto(photo)),
      cover: convertImageFromDto(dto.cover),
      author: UserEntity.fromDto(dto.owner),
      currentUserRole: dto.currentUserRole ?? undefined,
      stripeAccountStatus: dto.stripeAccountStatus ?? undefined,
      stripeAccountCurrency: dto.stripeAccountCurrency ?? undefined,
      stripeMaximumAmount: dto.stripeMaximumAmount ?? undefined,
      stripeMinimumAmount: dto.stripeMinimumAmount ?? undefined,
      accountError: dto.accountError ?? undefined,
      stripeAccountId: dto.stripeAccountId ?? undefined,
      stripePlatform: dto.stripePlatform,
      location: dto.location ?? undefined,
      slotGeneratorInWeeks: dto.slotGeneratorInWeeks ?? undefined,
      zoneId: dto.zoneId ?? null,
      waiver: dto.waiver ?? null,
      invitationLink: dto.invitationLink,
      showSessionAttendees: dto.showSessionAttendees,
      taxStatus: dto.taxStatus ?? null,
      payoutFrequencyType: dto.payoutFrequencyType,
      payoutFrequencyDayCalendar: dto.payoutFrequencyDayCalendar ?? null,
      weeklyAnchorStripe: dto.weeklyAnchorStripe ?? null,
      biweeklyPeriod: dto.biweeklyPeriod ?? null,
      createdAt: dto.createdAt,
    })
  }

  getBoxFormik(): BoxFormikType {
    return {
      name: this.name,
      ...convertAddressToFormik(this.address),
      phoneNumber: this.phoneNumberWithPrefix.number,
      phoneNumberCountryCode: this.phoneNumberWithPrefix.countryCode,
      email: this.email,
      siren: this.siren,
      description: this.description,
      location: this.location ?? null,
      zoneId: this.zoneId ?? dayjs.tz.guess(),
      logo: {
        url: this.logo.url,
        key: this.logo.key ?? null,
        type: MediaType.S3_IMAGE,
        filename: null,
        isUploading: false,
        file: null,
      },
      cover: {
        url: this.cover.url,
        key: this.cover.key ?? null,
        type: MediaType.S3_IMAGE,
        filename: null,
        isUploading: false,
        file: null,
      },
      boxPhotos: this.boxPhotos.map((image) => ({
        url: image.url,
        key: image.key ?? null,
        type: MediaType.S3_IMAGE,
        filename: null,
        isUploading: false,
        file: null,
      })),
    }
  }

  getBoxSettingsDraft(): BoxSettingsFormFields {
    return {
      paymentMethodPreferences: this.paymentMethodPreferences,
      slotGeneratorInWeeks: this.slotGeneratorInWeeks ?? 1,
      showSessionAttendees: this.showSessionAttendees,
      payoutFrequency: {
        type: this.payoutFrequencyType,
        dayCalendar: this.payoutFrequencyDayCalendar ?? 5,
        weeklyAnchorStripe: this.weeklyAnchorStripe ?? WeeklyAnchorStripe.MONDAY,
        biweeklyPeriod: this.biweeklyPeriod ?? BiweeklyPeriod.FIRST_AND_THIRD_WEEK,
      },
    }
  }

  getBoxWaiverDraft(): BoxWaiverFormFields {
    return {
      waiver: this.waiver
        ? {
            file: null,
            url: this.waiver.url,
            key: this.waiver.key,
            filename: this.waiver.originalFileName,
            isUploading: false,
            type: MediaType.S3_PDF,
          }
        : null,
    }
  }

  /**
   * Get menu permissions based on current user role
   */
  getPermissions(): BoxMenuPermissions {
    if (!this.currentUserRole) {
      return {
        canSeeTimetables: false,
        canSeeOffers: false,
        canSeeMembers: false,
        canSeeFinancial: false,
        canSeeGymInformation: false,
        canSeeShop: false,
      }
    }

    const allowedPermissions = roleBoxStaffPermissions[this.currentUserRole]

    return {
      canSeeTimetables: allowedPermissions.includes(BoxStaffPermissions.SEE_TIMETABLES),
      canSeeOffers: allowedPermissions.includes(BoxStaffPermissions.SEE_OFFERS),
      canSeeMembers: allowedPermissions.includes(BoxStaffPermissions.SEE_MEMBERS),
      canSeeFinancial: allowedPermissions.includes(BoxStaffPermissions.SEE_FINANCIAL),
      canSeeGymInformation: allowedPermissions.includes(BoxStaffPermissions.SEE_GYM_INFORMATION),
      canSeeShop: allowedPermissions.includes(BoxStaffPermissions.SEE_SHOP),
    }
  }
}

export class BoxItemEntity {
  _type: typeof BoxTypeOf
  id: string
  logo: HUImage
  cover: HUImage
  name: string
  address: Address
  phoneNumberWithPrefix: PhoneNumberWithPrefix
  email: string
  role: BoxStaffRole

  private constructor(boxItem: BoxItemEntity) {
    this._type = BoxTypeOf
    this.id = boxItem.id
    this.logo = boxItem.logo
    this.cover = boxItem.cover
    this.name = boxItem.name
    this.address = boxItem.address
    this.phoneNumberWithPrefix = boxItem.phoneNumberWithPrefix
    this.email = boxItem.email
    this.role = boxItem.role
  }

  static fromDto(dto: BoxItemDto): BoxItemEntity {
    return new BoxItemEntity({
      _type: BoxTypeOf,
      id: dto.id,
      logo: convertImageFromDto(dto.logo),
      cover: convertImageFromDto(dto.cover),
      name: dto.name,
      address: dto.address,
      phoneNumberWithPrefix: dto.phoneNumberWithPrefix,
      email: dto.email,
      role: dto.role,
    })
  }
}
